import React, {useEffect, useState} from "react";
import {Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Link, Stack, Typography} from "@mui/material";
import {Link as RouterLink, Navigate} from "react-router-dom";
import {useAuth} from "../../contexts/AuthContext";
import {fetchPublicMarketPrices, MarketPriceRow} from "../../api/marketPrice";
import {fetchPublicSeedAvailability, SeedAvailabilityRow} from "../../api/seedAvailability";
import {format, parseISO} from "date-fns";
import {
  getMarketSnapshot,
  getPolicyAlerts,
  getPublicNews,
  getRiskSignals,
  getTrendPulse,
  MarketNewsItem,
  MarketSnapshotItem,
  PolicyAlertItem,
  RiskSignalItem,
  TrendDirection,
  TrendPulseItem,
} from "../../api/publicDashboard";

interface PublicDashboardData {
  prices: MarketPriceRow[];
  seedAvailability: SeedAvailabilityRow[];
  snapshot: MarketSnapshotItem[];
  trendPulse: TrendPulseItem[];
  news: MarketNewsItem[];
  risks: RiskSignalItem[];
  policyAlerts: PolicyAlertItem[];
}

const trendColor = (direction: TrendDirection): "success" | "error" | "default" => {
  if (direction === "up") return "success";
  if (direction === "down") return "error";
  return "default";
};

const formatSeedDate = (iso: string): string => {
  try {
    return format(parseISO((iso || "").slice(0, 10)), "d-MMM");
  } catch {
    return (iso || "").slice(0, 10);
  }
};

const PublicDashboardPage: React.FC = () => {
  const {isAuthenticated} = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState<PublicDashboardData>({
    prices: [],
    seedAvailability: [],
    snapshot: [],
    trendPulse: [],
    news: [],
    risks: [],
    policyAlerts: [],
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [prices, seedAvailability, snapshot, trendPulse, news, risks, policyAlerts] = await Promise.all([
          fetchPublicMarketPrices(5),
          fetchPublicSeedAvailability(5),
          getMarketSnapshot(),
          getTrendPulse(),
          getPublicNews(),
          getRiskSignals(),
          getPolicyAlerts(),
        ]);
        setData({prices, seedAvailability, snapshot, trendPulse, news, risks, policyAlerts});
      } catch (e: any) {
        setError(e?.message || "Unable to load market widgets right now.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box sx={{minHeight: "100vh", bgcolor: "#f7f9fc", p: {xs: 2, md: 4}}}>
      <Card sx={{mb: 3}}>
        <CardContent>
          <Stack direction={{xs: "column", md: "row"}} alignItems={{xs: "flex-start", md: "center"}} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h4" sx={{fontWeight: 700}}>
                Market Intelligence Dashboard
              </Typography>
              <Typography color="text.secondary">Public overview with market news, trends, and risk signals.</Typography>
            </Box>
            <Button component={RouterLink} to="/login" variant="contained" size="large">
              Login
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction={{xs: "column", md: "row"}} justifyContent="space-between" alignItems={{xs: "flex-start", md: "center"}}>
                  <Typography variant="h6" gutterBottom>
                    Today's Market Prices
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <Link component={RouterLink} to="/login" underline="hover">
                      Login
                    </Link>{" "}
                    for more prices
                  </Typography>
                </Stack>
                {data.prices.length === 0 ? (
                  <Box>
                    <Typography color="text.secondary">No public prices available right now.</Typography>
                    <Button component={RouterLink} to="/login" variant="outlined" sx={{mt: 1.5}}>
                      Login to view full list
                    </Button>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {data.prices.map((row) => (
                      <Box
                        key={row.id}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {xs: "1fr", md: "1.2fr 1.2fr 1.2fr 0.8fr"},
                          gap: 1,
                          p: 1.5,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          alignItems: "center",
                        }}>
                        <Typography variant="body2" color="text.secondary">
                          {row.area?.name || "Area"}
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 600}}>
                          {row.commodity?.name || "Commodity"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.pricingMode === "COUNT_WISE" ? `${row.count || "-"}C ${row.label ? `(${row.label})` : ""}` : row.label || "PER_KG"}
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 700}}>
                          ₹ {Math.round(Number(row.pricePerKg))}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction={{xs: "column", md: "row"}} justifyContent="space-between" alignItems={{xs: "flex-start", md: "center"}}>
                  <Typography variant="h6" gutterBottom>
                    Seed Availability
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <Link component={RouterLink} to="/login" underline="hover">
                      Login
                    </Link>{" "}
                    for full hatchery details
                  </Typography>
                </Stack>
                {data.seedAvailability.length === 0 ? (
                  <Box>
                    <Typography color="text.secondary">No public seed listings right now.</Typography>
                    <Button component={RouterLink} to="/login" variant="outlined" sx={{mt: 1.5}}>
                      Login to view full list
                    </Button>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {data.seedAvailability.map((row) => (
                      <Box
                        key={row.id}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {xs: "1fr", md: "1fr 1.2fr 1.2fr 0.7fr 0.9fr"},
                          gap: 1,
                          p: 1.5,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          alignItems: "center",
                        }}>
                        <Typography variant="body2" color="text.secondary">
                          {row.area?.name || "Area"}
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 600}}>
                          {row.hatcheryName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.location}
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 700}}>
                          ₹{Number(row.price).toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatSeedDate(row.availabilityDate)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Market Snapshot
                </Typography>
                {data.snapshot.length === 0 ? (
                  <Typography color="text.secondary">No snapshot data available.</Typography>
                ) : (
                  <Grid container spacing={2}>
                    {data.snapshot.map((item) => (
                      <Grid key={item.id} item xs={12} sm={6} md={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">
                                {item.label}
                              </Typography>
                              <Chip size="small" label={item.period} />
                            </Stack>
                            <Typography variant="h5" sx={{fontWeight: 700, mt: 1}}>
                              {item.value}
                            </Typography>
                            <Chip size="small" color={trendColor(item.direction)} label={item.direction.toUpperCase()} sx={{mt: 1}} />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{height: "100%"}}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Trend Pulse (7D / 30D / 90D)
                </Typography>
                {data.trendPulse.length === 0 ? (
                  <Typography color="text.secondary">No trend pulse available.</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {data.trendPulse.map((trend) => (
                      <Box key={trend.id} sx={{p: 1.5, border: "1px solid #e0e0e0", borderRadius: 1}}>
                        <Typography variant="body2" sx={{mb: 1}}>
                          {trend.metric}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Chip size="small" label={`7D: ${trend.d7}`} color={trendColor(trend.d7)} />
                          <Chip size="small" label={`30D: ${trend.d30}`} color={trendColor(trend.d30)} />
                          <Chip size="small" label={`90D: ${trend.d90}`} color={trendColor(trend.d90)} />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{height: "100%"}}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Weather & Production Risk
                </Typography>
                {data.risks.length === 0 ? (
                  <Typography color="text.secondary">No risk signals available.</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {data.risks.map((risk) => (
                      <Box key={risk.id} sx={{p: 1.5, border: "1px solid #e0e0e0", borderRadius: 1}}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{fontWeight: 600}}>
                            {risk.region}
                          </Typography>
                          <Chip
                            size="small"
                            label={risk.level.toUpperCase()}
                            color={risk.level === "high" ? "error" : risk.level === "medium" ? "warning" : "success"}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                          {risk.context}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{height: "100%"}}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Market News
                </Typography>
                {data.news.length === 0 ? (
                  <Typography color="text.secondary">No market news available.</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {data.news.map((news) => (
                      <Box key={news.id} sx={{p: 1.5, border: "1px solid #e0e0e0", borderRadius: 1}}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 0.5}}>
                          <Chip size="small" label={news.tag} />
                          <Typography variant="caption" color="text.secondary">
                            {news.publishedAt} - {news.source}
                          </Typography>
                        </Stack>
                        <Typography variant="body2">{news.title}</Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{height: "100%"}}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Policy & Trade Alerts
                </Typography>
                {data.policyAlerts.length === 0 ? (
                  <Typography color="text.secondary">No policy alerts available.</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {data.policyAlerts.map((alert) => (
                      <Alert key={alert.id} severity={alert.severity}>
                        <Typography variant="body2" sx={{fontWeight: 600}}>
                          {alert.title}
                        </Typography>
                        <Typography variant="caption">{alert.publishedAt}</Typography>
                      </Alert>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction={{xs: "column", md: "row"}} justifyContent="space-between" alignItems={{xs: "flex-start", md: "center"}} spacing={2}>
                  <Box>
                    <Typography variant="h6">Access full prices and advanced analytics</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sign in to view detailed market prices, seed availability, tenant dashboards, and operations modules.
                    </Typography>
                  </Box>
                  <Button component={RouterLink} to="/login" variant="contained">
                    Login
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default PublicDashboardPage;
